import { useState, useEffect, useCallback, useRef } from "react";
import { Button, TextField, MenuItem, Select, FormControl, SelectChangeEvent } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { PiCaretRightBold, PiCheckCircle } from "react-icons/pi";
import { enqueueSnackbar } from "notistack";
import { debounce } from "lodash";


interface UpdateShippingInfoProps {
    subscriptionData: any;
    onGoBack: () => void;
    onUpdateSuccess: () => void;
}

export default function UpdateShippingInfo(props: UpdateShippingInfoProps) {
    const { subscriptionData, onGoBack, onUpdateSuccess } = props;
    const [loading, setLoading] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    
    const [countries, setCountries] = useState<any[]>([]);
    const [regions, setRegions] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [cityInputValue, setCityInputValue] = useState('');
    
    // Refs para la lógica de auto-selección
    const citiesRef = useRef<any[]>([]);
    const pendingAutoSelectRef = useRef<{keyword: string, timestamp: number} | null>(null);

    const [formValues, setFormValues] = useState({
        client_name: '',
        client_lastname: '',
        country_id: '',
        region_id: '',
        city: '',
        city_id: '',
        postal_code: '',
        phone: '',
    });

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric',
            timeZone: 'UTC'
        });
    };

    /*=======================================
    INITIALIZE - Load countries first, then set form values
    =======================================*/
    useEffect(() => {
        if (!isInitialized && subscriptionData) {
            initializeForm();
        }
    }, [subscriptionData, isInitialized]);

    const initializeForm = async () => {
        // Load countries first
        const countriesData = await loadCountries();
        
        // Set initial form values from subscriptionData
        const initialValues = {
            client_name: subscriptionData?.client_name || '',
            client_lastname: subscriptionData?.client_lastname || '',
            country_id: subscriptionData?.country_id?.toString() || '',
            region_id: subscriptionData?.region_id?.toString() || '',
            city: subscriptionData?.city_name || '',
            city_id: subscriptionData?.city_id?.toString() || '',
            postal_code: subscriptionData?.postal_code || '',
            phone: subscriptionData?.phone || '',
        };
        
        setFormValues(initialValues);
        
        // Set city input value for autocomplete
        if (subscriptionData?.city_name) {
            setCityInputValue(subscriptionData.city_name);
        }
        
        // If there's a country_id, load regions
        if (initialValues.country_id) {
            await loadRegions(initialValues.country_id);
        }
        
        setIsInitialized(true);
    };

    /*=======================================
    LOAD COUNTRIES
    =======================================*/
    const loadCountries = (): Promise<any[]> => {
        return new Promise((resolve) => {
            ApiRequest({
                url: '/countries/getCountries',
                method: 'GET',
                setResponse: (response: any) => {
                    const data = response || [];
                    setCountries(data);
                    resolve(data);
                }
            });
        });
    };

    /*=======================================
    LOAD REGIONS
    =======================================*/
    const loadRegions = (countryId: string): Promise<any[]> => {
        return new Promise((resolve) => {
            ApiRequest({
                url: '/countries/getRegions',
                method: 'GET',
                query: { country_id: countryId },
                setResponse: (response: any) => {
                    const data = response?.list || [];
                    setRegions(data);
                    resolve(data);
                }
            });
        });
    };

    /*=======================================
    SEARCH CITIES (Predictive)
    =======================================*/
    const searchCities = (keyword: string, regionId: string) => {
        if (!regionId) {
            setCities([]);
            citiesRef.current = [];
            return;
        }
        setCitiesLoading(true);
        ApiRequest({
            url: '/countries/getCities',
            method: 'GET',
            query: { 
                region_id: regionId,
                keyword: keyword 
            },
            setResponse: (response: any) => {
                const citiesData = response || [];
                setCities(citiesData);
                citiesRef.current = citiesData;
                setCitiesLoading(false);
                
                // Verificar si hay una auto-selección pendiente
                if (pendingAutoSelectRef.current && citiesData.length > 0) {
                    const { keyword: pendingKeyword, timestamp } = pendingAutoSelectRef.current;
                    const now = Date.now();
                    
                    // Verificar que la respuesta corresponde a la búsqueda pendiente
                    // y que no haya pasado demasiado tiempo (5 segundos máximo)
                    if (keyword.trim() === pendingKeyword.trim() && (now - timestamp) < 5000) {
                        // Seleccionar la primera ciudad automáticamente
                        const firstCity = citiesData[0];
                        setFormValues(prev => ({
                            ...prev,
                            city: firstCity.city_name,
                            city_id: firstCity.city_id?.toString() || '',
                        }));
                        setCityInputValue(firstCity.city_name);
                    }
                    pendingAutoSelectRef.current = null;
                }
            }
        });
    };

    // Debounced search for cities
    const debouncedSearchCities = useCallback(
        debounce((keyword: string, regionId: string) => {
            searchCities(keyword, regionId);
        }, 300),
        []
    );

    /*=======================================
    HANDLE CITY INPUT CHANGE
    =======================================*/
    const handleCityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCityInputValue(value);
        
        // Limpiar city_id cuando el usuario está escribiendo
        setFormValues(prev => ({
            ...prev,
            city: value,
            city_id: '',
        }));
        
        if (value && formValues.region_id) {
            debouncedSearchCities(value, formValues.region_id);
        }
    };

    /*=======================================
    HANDLE CITY BLUR - Auto-select first city
    =======================================*/
    const handleCityBlur = () => {
        const currentValue = cityInputValue.trim();
        
        if (currentValue === '') {
            // Si el input está vacío, limpiar
            setFormValues(prev => ({
                ...prev,
                city: '',
                city_id: '',
            }));
            return;
        }
        
        // Si ya hay ciudades disponibles, seleccionar la primera
        if (citiesRef.current.length > 0) {
            const firstCity = citiesRef.current[0];
            setFormValues(prev => ({
                ...prev,
                city: firstCity.city_name,
                city_id: firstCity.city_id?.toString() || '',
            }));
            setCityInputValue(firstCity.city_name);
        } else {
            // Si no hay resultados aún, marcar para auto-selección cuando llegue la respuesta
            pendingAutoSelectRef.current = {
                keyword: currentValue,
                timestamp: Date.now()
            };
        }
    };

    /*=======================================
    HANDLE INPUT CHANGE
    =======================================*/
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormValues(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    /*=======================================
    HANDLE SELECT CHANGE
    =======================================*/
    const handleSelectChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        
        if (name === 'country_id') {
            // Reset region and city when country changes
            setFormValues(prev => ({
                ...prev,
                country_id: value,
                region_id: '',
                city: '',
                city_id: '',
            }));
            setRegions([]);
            setCities([]);
            setCityInputValue('');
            if (value) {
                loadRegions(value);
            }
        } else if (name === 'region_id') {
            // Reset city when region changes
            setFormValues(prev => ({
                ...prev,
                region_id: value,
                city: '',
                city_id: '',
            }));
            setCities([]);
            setCityInputValue('');
        } else {
            setFormValues(prev => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    /*=======================================
    SUBMIT
    =======================================*/
    const submitUpdateShippingInfo = () => {
        // Validation
        if (!formValues.client_name || !formValues.client_lastname) {
            enqueueSnackbar('Please enter your full name', { variant: 'error' });
            return;
        }

        if (!formValues.country_id) {
            enqueueSnackbar('Please select a country', { variant: 'error' });
            return;
        }

        if (!formValues.region_id) {
            enqueueSnackbar('Please select a state/province', { variant: 'error' });
            return;
        }

        if (!formValues.postal_code) {
            enqueueSnackbar('Please enter a zip code', { variant: 'error' });
            return;
        }

        setLoading(true);
        ApiRequest({
            url: '/subscriptions/updateShippingInfo',
            method: 'POST',
            body: {
                subscription_id: subscriptionData?.subscription_id,
                client_name: formValues.client_name,
                client_lastname: formValues.client_lastname,
                country_id: formValues.country_id,
                region_id: formValues.region_id,
                city_id: formValues.city_id || 0,
                postal_code: formValues.postal_code,
                phone: formValues.phone,
            },
            setResponse: (response: any) => {
                setLoading(false);
                if (response.status === "success") {
                    setUpdated(true);
                } else {
                    enqueueSnackbar(response.message || "Failed to update shipping information", { variant: 'error' });
                }
            }
        });
    };

    return (
        <div className="pc-subscription-section">
            <div className="pc-cancel-breadcrumb">
                <span>BILLING</span>
                <PiCaretRightBold />
                <span>SHIPPING UPDATE</span>
            </div>
            
                {updated ? (
                    <div className="pc-cancel-success-overlay">
                        <PiCheckCircle className="pc-cancel-success-icon" />
                        <h1 className="pc-cancel-success-title">Shipping Update Successful</h1>
                        <p className="pc-cancel-success-message">Updates to your shipment info have been made.</p>
                        <Button
                            variant="contained"
                            onClick={onUpdateSuccess}
                            fullWidth
                            style={{
                                backgroundColor: subscriptionData?.primary_color || '#303030',
                                color: '#fff',
                                borderRadius: '10px',
                                padding: '10px 40px',
                                fontSize: '16px',
                                textTransform: 'none',
                                marginTop: '30px',
                                maxWidth: '400px'
                            }}
                        >
                            Back To Account
                        </Button>
                    </div>
                ) : (
                    <div className="pc-update-form">
                        <h1 className="pc-cancel-title">Update Shipping Information</h1>

                        <div className="pc-update-form">
                            <div className="pc-form-row">
                                <div className="pc-form-field">
                                    <label>First Name</label>
                                    <TextField
                                        name="client_name"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={formValues.client_name}
                                        onChange={handleInputChange}
                                        placeholder="John"
                                    />
                                </div>
                                <div className="pc-form-field">
                                    <label>Last Name</label>
                                    <TextField
                                        name="client_lastname"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={formValues.client_lastname}
                                        onChange={handleInputChange}
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="pc-form-row">
                                <div className="pc-form-field">
                                    <label>Country</label>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            name="country_id"
                                            value={formValues.country_id}
                                            onChange={handleSelectChange}
                                            displayEmpty
                                            MenuProps={{
                                                PaperProps: {
                                                    style: { zIndex: 99999999 }
                                                },
                                                style: { zIndex: 99999999 }
                                            }}
                                        >
                                            <MenuItem value="" disabled>Select Country</MenuItem>
                                            {countries.map((country: any) => (
                                                <MenuItem key={country.country_id} value={country.country_id.toString()}>
                                                    {country.country_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                                <div className="pc-form-field">
                                    <label>State/Province</label>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            name="region_id"
                                            value={formValues.region_id}
                                            onChange={handleSelectChange}
                                            displayEmpty
                                            disabled={!formValues.country_id}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: { zIndex: 99999999 }
                                                },
                                                style: { zIndex: 99999999 }
                                            }}
                                        >
                                            <MenuItem value="" disabled>Select State/Province</MenuItem>
                                            {regions.map((region: any) => (
                                                <MenuItem key={region.region_id} value={region.region_id.toString()}>
                                                    {region.region_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>

                            <div className="pc-form-row">
                                <div className="pc-form-field">
                                    <label>City</label>
                                    <TextField
                                        name="city"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={cityInputValue}
                                        onChange={handleCityInputChange}
                                        onBlur={handleCityBlur}
                                        disabled={!formValues.region_id}
                                        placeholder="Start typing city name..."
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="pc-form-field">
                                    <label>Zip Code</label>
                                    <TextField
                                        name="postal_code"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={formValues.postal_code}
                                        onChange={handleInputChange}
                                        placeholder="12345"
                                    />
                                </div>
                            </div>

                            <div className="pc-form-row">
                                <div className="pc-form-field pc-form-field-full">
                                    <label>Phone Number</label>
                                    <TextField
                                        name="phone"
                                        type="tel"
                                        size="small"
                                        fullWidth
                                        value={formValues.phone}
                                        onChange={handleInputChange}
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="pc-update-note">
                            You can update your shipping info until {formatDate(subscriptionData?.latest_next_payment_date)}.
                        </p>

                        <div className="pc-cancel-actions">
                            <Button
                                variant="contained"
                                onClick={submitUpdateShippingInfo}
                                disabled={loading}
                                fullWidth
                                style={{
                                    backgroundColor: subscriptionData?.primary_color || '#303030',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    padding: '10px 40px',
                                    fontSize: '16px',
                                    textTransform: 'none',
                                }}
                            >
                                Update
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={onGoBack}
                                fullWidth
                                style={{
                                    borderColor: subscriptionData?.primary_color || '#303030',
                                    color: subscriptionData?.primary_color || '#303030',
                                    borderRadius: '10px',
                                    padding: '10px 40px',
                                    fontSize: '16px',
                                    textTransform: 'none',
                                    marginTop: '15px'
                                }}
                            >
                                Go back
                            </Button>
                        </div>
                    </div>
                )}
        </div>
    );
}
